import { useEffect, useRef, useState } from 'react';

import { SabreLogo } from './SabreLogo';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'ru', label: 'Pyccкий' },
  { value: 'ja', label: '日本語' },
  { value: 'cn', label: '中文（中国）' },
  { value: 'hk', label: '中文 (繁體)' },
  { value: 'kr', label: '한국어' },
];

const LANGUAGE_STORAGE_KEY = 'sabre.lang';

export function SparkFooter() {
  const [language, setLanguage] = useState(() => {
    try {
      return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? 'en';
    } catch {
      return 'en';
    }
  });
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (value: string) => {
    setLanguage(value);
    setOpen(false);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, value);
    } catch {
      // A private window without storage still gets the in-memory choice.
    }
  };

  const active = LANGUAGES.find((item) => item.value === language) ?? LANGUAGES[0];

  return (
    <footer className="spark-footer">
      <div className="spark-footer__content">
        <SabreLogo width={74} title="Sabre" />
        <div className="spark-footer__copyright">© {new Date().getFullYear()} Sabre GLBL Inc.</div>
        <ul className="spark-footer__list">
          <li className="spark-footer__list-item">
            <a className="spark-footer__list-link" href="/privacy-policy">
              Privacy policy
            </a>
          </li>
          <li className="spark-footer__list-item">
            <a className="spark-footer__list-link" href="/contact/agent">
              Contact Us
            </a>
          </li>
          <li className="spark-footer__list-item">
            <a className="spark-footer__list-link" href="/trademark">
              Trademark
            </a>
          </li>
        </ul>

        <div className="spark-popover-group" ref={popoverRef}>
          <div className="spark-popover">
            <button
              type="button"
              className="spark-popover__toggle"
              aria-label="Select language"
              aria-expanded={open}
              aria-haspopup="true"
              onClick={() => setOpen((value) => !value)}
            >
              <span>{active.label}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                <path fill="currentColor" d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z" />
              </svg>
            </button>
            {open && (
              <div className="spark-popover__content" role="dialog" aria-label="Languages">
                <ul className="spark-popover__list">
                  {LANGUAGES.map((item) => (
                    <li key={item.value}>
                      <button
                        type="button"
                        className={`spark-popover__list-link${
                          item.value === language ? ' spark-popover__list-link--active' : ''
                        }`}
                        onClick={() => choose(item.value)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
